import { createApi, type Api } from "@ap/api";
import { useAuth } from "@ap/ui-kit";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type DependencyList, type ReactNode } from "react";

const ApiContext = createContext<Api | null>(null);

export function ApiProvider({ children, onUnauthorized }: { children: ReactNode; onUnauthorized?: () => void }) {
  const { getToken } = useAuth();
  // Same origin, spelled out so the client also works where relative URLs are not resolved (tests).
  const api = useMemo(() => createApi({ getToken, onUnauthorized, baseUrl: globalThis.location?.origin ?? "" }), [getToken, onUnauthorized]);
  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}

export function useApi(): Api {
  const api = useContext(ApiContext);
  if (!api) throw new Error("useApi outside ApiProvider");
  return api;
}

export interface Resource<T> {
  data: T | undefined;
  error: Error | null;
  loading: boolean;
  reload: () => void;
  setData: (fn: (prev: T | undefined) => T | undefined) => void;
}

/** Loads data with the API client; cancels stale results when deps change. */
export function useResource<T>(load: (api: Api) => Promise<T>, deps: DependencyList = []): Resource<T> {
  const api = useApi();
  const [data, setDataState] = useState<T | undefined>(undefined);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });
  useEffect(() => {
    let alive = true;
    setLoading(true);
    loadRef
      .current(api)
      .then((d) => {
        if (alive) {
          setDataState(d);
          setError(null);
        }
      })
      .catch((e: unknown) => alive && setError(e instanceof Error ? e : new Error(String(e))))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, tick, ...deps]);
  const reload = useCallback(() => setTick((x) => x + 1), []);
  const setData = useCallback((fn: (prev: T | undefined) => T | undefined) => setDataState(fn), []);
  return { data, error, loading, reload, setData };
}
